import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { localeFor } from "../lib/dateLocale";
import { Pulse, Alarm, ArrowLeft, Camera, Image as ImageIcon, CircleNotch, QrCode, PaperPlaneTilt, Trophy, Tag, Timer as TimerIcon, Hamburger, Calendar, CaretDown, CaretUp, CheckCircle, CloudRain, CloudSun, Coffee, Drop, Flag, Footprints, MapPin, Moon, Package, Pill, AirplaneTilt, ShareNetwork, TShirt, ShieldCheck, ShoppingBag, Snowflake, Sun, Timer, TrendDown, ForkKnife, Watch, Plant, Lightning } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import {
  useGenerateShareCard,
  useRaceWeek,
  useShareToCoach,
  useToggleRaceWeekItem,
  useUpdateRaceDayFields,
  type ChecklistStage,
  type ChecklistItem as RaceItem,
  type RaceDayPlan,
} from "../raceWeekApi";
import { useGear } from "../gearApi";
import { RaceCountdownTimer } from "../components/RaceCountdownTimer";
import { formatDistance, unitLabel, useUnitsPreference } from "../lib/useUnitsPreference";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";

const ICON_MAP: Record<
  string,
  React.FC<{ className?: string; strokeWidth?: number }>
> = {
  Pulse,
  Alarm,
  Hamburger,
  Calendar,
  CloudRain,
  CloudSun,
  Coffee,
  Drop,
  Flag,
  Footprints,
  MapPin,
  Moon,
  Package,
  Pill,
  AirplaneTilt,
  ShareNetwork,
  TShirt,
  ShieldCheck,
  ShoppingBag,
  Snowflake,
  Sun,
  Timer,
  TrendDown,
  ForkKnife,
  Watch,
  Plant,
  Lightning,
};

function fmtRaceDate(iso: string | null, lang?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(localeFor(lang), {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function ItemRow({ item }: { item: RaceItem }) {
  const toggle = useToggleRaceWeekItem();
  const [open, setOpen] = useState(false);
  const Icon = ICON_MAP[item.icon] ?? CheckCircle;
  const { t } = useTranslation("race_week");

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggle.mutate({ itemKey: item.key, checked: !item.checked });
  };

  return (
    <div
      className={`rounded-card border transition-all ${
        item.checked
          ? "bg-emerald-500/5 border-emerald-500/30"
          : item.critical
            ? "bg-amber-500/5 border-amber-500/30"
            : "bg-bg-surface border-bg-border"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-3 py-2.5 flex items-start gap-3"
      >
        <button
          type="button"
          onClick={handleToggle}
          className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
            item.checked
              ? "bg-emerald-500 border-emerald-500 text-bg-base"
              : "border-bg-divider hover:border-fg-muted"
          }`}
          aria-label={item.checked ? t("unmark") : t("mark")}
        >
          {item.checked && <CheckCircle className="w-4 h-4" />}
        </button>
        <Icon
          className={`shrink-0 w-4 h-4 mt-0.5 ${
            item.checked ? "text-emerald-300" : item.critical ? "text-amber-300" : "text-fg-muted"
          }`}
          strokeWidth={1.75}
        />
        <div className="min-w-0 flex-1">
          <div
            className={`text-sm font-medium ${
              item.checked ? "text-fg-secondary line-through" : "text-fg-primary"
            }`}
          >
            {item.title}
            {item.critical && !item.checked && (
              <span className="ml-1.5 text-[9px] uppercase tracking-wider text-amber-300/80">
                {t("important_badge")}
              </span>
            )}
          </div>
          {open && (
            <div className="text-[11px] text-fg-muted mt-1 leading-relaxed">{item.hint}</div>
          )}
        </div>
        {open ? (
          <CaretUp className="w-3.5 h-3.5 text-fg-muted shrink-0 mt-1" />
        ) : (
          <CaretDown className="w-3.5 h-3.5 text-fg-muted shrink-0 mt-1" />
        )}
      </button>
    </div>
  );
}

function StageBlock({ stage }: { stage: ChecklistStage }) {
  const [collapsed, setCollapsed] = useState(!stage.is_current);
  const { t } = useTranslation("race_week");
  const pct = stage.total_count > 0
    ? Math.round((stage.completed_count / stage.total_count) * 100)
    : 0;

  return (
    <section className="space-y-2">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 rounded-card border px-3 py-2 transition-colors ${
          stage.is_current
            ? "bg-accent/10 border-accent/60 text-fg-primary"
            : "bg-bg-raised border-bg-border text-fg-muted hover:text-fg-primary"
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-[3px] ${
              stage.is_current ? "bg-accent text-bg-base" : "bg-bg-surface ring-1 ring-bg-divider"
            }`}
          >
            {stage.key === "race_day"
              ? t("race_start_label")
              : stage.key === "T_1"
                ? "T-1"
                : stage.key.replace("T_", "T-")}
          </span>
          <span className="text-sm font-medium truncate">
            {t(`stage.${stage.key}`, { defaultValue: stage.title })}
          </span>
          <span className="text-[11px] text-fg-muted shrink-0 num-display">
            {stage.completed_count}/{stage.total_count} · {pct}%
          </span>
        </div>
        {collapsed ? (
          <CaretDown className="w-4 h-4 shrink-0" />
        ) : (
          <CaretUp className="w-4 h-4 shrink-0" />
        )}
      </button>
      {!collapsed && (
        <div className="space-y-1.5 pl-1">
          {stage.items.map((it) => (
            <ItemRow key={it.key} item={it} />
          ))}
        </div>
      )}
    </section>
  );
}

function fmtPace(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function fmtTime(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function NutritionIcon({ item }: { item: string }) {
  if (item === "water") return <Drop className="w-3 h-3 text-blue-300 shrink-0" />;
  if (item === "gel") return <Lightning className="w-3 h-3 text-amber-300 shrink-0" />;
  if (item === "salt") return <Pill className="w-3 h-3 text-fg-muted shrink-0" />;
  if (item === "electrolyte") return <Drop className="w-3 h-3 text-cyan-300 shrink-0" />;
  return <span className="w-3 h-3 shrink-0">•</span>;
}


function RaceDayBlock({
  plan,
  daysUntil,
}: {
  plan: RaceDayPlan;
  daysUntil: number;
}) {
  const { t } = useTranslation("race_week");
  const { unit } = useUnitsPreference();
  const update = useUpdateRaceDayFields();
  const share = useShareToCoach();
  const [bib, setBib] = useState(plan.bib_number ?? "");
  const [tracker, setTracker] = useState(plan.tracker_link ?? "");
  const [showQR, setShowQR] = useState(false);
  const [shared, setShared] = useState(false);

  const onSave = () => {
    update.mutate({
      bib_number: bib.trim(),
      tracker_link: tracker.trim(),
    });
  };

  const onShare = async () => {
    setShared(false);
    try {
      await share.mutateAsync({});
      setShared(true);
      setTimeout(() => setShared(false), 3000);
    } catch {
      // silent: error surfaced via share.isError mutation state
    }
  };

  const dayLabel =
    daysUntil <= 0
      ? t("today_start")
      : daysUntil === 1
        ? t("tomorrow_start")
        : t("days_to_start", { count: daysUntil });

  return (
    <section className="rounded-card bg-gradient-to-br from-emerald-500/10 via-cyan-500/5 to-violet-500/10 ring-1 ring-emerald-500/30 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Tag className="w-4 h-4 text-emerald-300" />
        <div>
          <div className="text-sm font-semibold">{t("morning_start")}</div>
          <div className="text-[10px] text-fg-muted">{dayLabel}</div>
        </div>
      </div>

      {/* BIB + tracker */}
      <div className="space-y-2">
        <div>
          <label className="text-[11px] text-fg-muted">{t("bib_label")}</label>
          <div className="flex gap-1.5 mt-1">
            <Input
              bare
              value={bib}
              onChange={(e) => setBib(e.target.value)}
              placeholder="A12345"
              className="flex-1 px-3 py-2 rounded-pill bg-bg-raised border border-bg-border text-sm num-display"
            />
            <button
              type="button"
              onClick={() => setShowQR((v) => !v)}
              disabled={!bib.trim()}
              className="px-3 py-2 rounded-pill bg-bg-raised border border-bg-border text-fg-muted disabled:opacity-50 hover:text-fg-primary"
              aria-label="QR"
            >
              <QrCode className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div>
          <label className="text-[11px] text-fg-muted">Tracker link (Garmin LiveTrack / Strava Beacon)</label>
          <Input
            bare
            value={tracker}
            onChange={(e) => setTracker(e.target.value)}
            placeholder="https://livetrack.garmin.com/session/..."
            className="w-full mt-1 px-3 py-2 rounded-pill bg-bg-raised border border-bg-border text-sm"
          />
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onSave}
            disabled={update.isPending}
            className="flex-1 px-3 py-2 rounded-pill bg-bg-raised text-fg-primary ring-1 ring-bg-divider text-sm disabled:opacity-50"
          >
            {update.isPending ? t("saving") : t("save")}
          </button>
          <button
            type="button"
            onClick={onShare}
            disabled={share.isPending}
            className="flex-1 px-3 py-2 rounded-pill bg-accent text-bg-base text-sm font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            <PaperPlaneTilt className="w-3.5 h-3.5" />
            {share.isPending ? t("sending") : shared ? t("sent") : t("to_coach")}
          </button>
        </div>
      </div>

      {/* QR */}
      {showQR && bib.trim() && (
        <div className="flex flex-col items-center gap-2 rounded-pill bg-white p-4 relative">
          <QRCodeSVG value={bib.trim()} size={160} level="M" />
          <div className="text-[10px] text-gray-700 num-display">{bib.trim()}</div>
          <div className="absolute bottom-1 right-2 text-[8px] text-gray-300 select-none">
            storica.coach
          </div>
        </div>
      )}

      {/* Pace plan */}
      {plan.target_finish_seconds && plan.splits_5k.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <TimerIcon className="w-4 h-4 text-cyan-300" />
            <div className="text-sm font-medium">
              {fmtTime(plan.target_finish_seconds)}
              {plan.target_pace_sec && (
                <span className="text-fg-muted text-xs ml-1.5 num-display">
                  ({fmtPace(plan.target_pace_sec)}{t("goal_pace")})
                </span>
              )}
            </div>
          </div>
          <div className="rounded-pill bg-bg-base/60 ring-1 ring-bg-divider overflow-hidden">
            <table className="w-full text-xs">
              <thead className="text-fg-muted bg-bg-raised/50">
                <tr>
                  <th className="px-2 py-1 text-left font-normal">{t("table_segment")}</th>
                  <th className="px-2 py-1 text-right font-normal">{t("table_pace")}</th>
                  <th className="px-2 py-1 text-right font-normal">{t("table_cumul")}</th>
                </tr>
              </thead>
              <tbody>
                {plan.splits_5k.map((s, i) => (
                  <tr
                    key={i}
                    className="border-t border-bg-divider/30"
                    title={s.note || undefined}
                  >
                    <td className="px-2 py-1.5 num-display">
                      {formatDistance(s.km, unit, s.km < 10 ? 1 : 0)} {unitLabel(unit)}
                    </td>
                    <td className="px-2 py-1.5 text-right num-display">
                      {fmtPace(s.target_pace_sec)}{t("goal_pace")}
                    </td>
                    <td className="px-2 py-1.5 text-right num-display text-fg-muted">
                      {fmtTime(s.cumulative_time_sec)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-fg-muted">
            {t("negative_split")}
          </div>
        </div>
      )}

      {/* Nutrition */}
      {plan.nutrition.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-fg-muted">
            {t("nutrition_header")}
          </div>
          <div className="grid grid-cols-2 gap-1">
            {plan.nutrition.map((n, i) => (
              <div
                key={i}
                className="flex items-center gap-1.5 rounded-pill bg-bg-base/60 ring-1 ring-bg-divider px-2 py-1 text-[11px]"
              >
                <NutritionIcon item={n.item} />
                <span className="num-display">T+{n.minute}min</span>
                <span className="text-fg-muted truncate flex-1">{n.note}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tracker apps */}
      {plan.tracker_apps.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-fg-muted">
            {t("live_tracking_header")}
          </div>
          <div className="flex flex-wrap gap-1">
            {plan.tracker_apps.map((app) => (
              <span
                key={app}
                className="px-2 py-0.5 rounded-[3px] bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-500/40 text-[10px]"
              >
                {app}
              </span>
            ))}
          </div>
        </div>
      )}

      {plan.notes.length > 0 && (
        <div className="text-[10px] text-fg-muted leading-relaxed pt-1 border-t border-bg-divider/30">
          {plan.notes.join(" · ")}
        </div>
      )}
    </section>
  );
}


function FinisherShareCard({ goalId }: { goalId: number }) {
  const { t } = useTranslation("race_week");
  const gen = useGenerateShareCard();
  const [photo, setPhoto] = useState<File | null>(null);
  const [fmt, setFmt] = useState<"story" | "square">("story");
  const [url, setUrl] = useState<string | null>(null);
  const [shared, setShared] = useState(false);

  const onPickPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setPhoto(f);
    setUrl(null);
  };

  const onGenerate = async () => {
    setShared(false);
    const r = await gen.mutateAsync({ goalId, fmt, photo });
    setUrl(r.url);
  };

  const onNativeShare = async () => {
    if (!url) return;
    const absolute = url.startsWith("http") ? url : `${window.location.origin}${url}`;
    // Web Share API level 2 (з файлом) — пробуємо, якщо браузер не вміє — fallback.
    try {
      const blob = await fetch(absolute).then((r) => r.blob());
      const file = new File([blob], `storica-finisher.png`, { type: "image/png" });
      const nav2 = navigator as Navigator & {
        canShare?: (data: ShareData) => boolean;
        share?: (data: ShareData) => Promise<void>;
      };
      if (nav2.canShare && nav2.share && nav2.canShare({ files: [file] })) {
        await nav2.share({
          files: [file],
          title: t("finish_title"),
          text: t("finish_subtitle"),
        });
        setShared(true);
        setTimeout(() => setShared(false), 3000);
        return;
      }
      if (navigator.share) {
        await navigator.share({ url: absolute, title: t("finish_title") });
        setShared(true);
        setTimeout(() => setShared(false), 3000);
        return;
      }
    } catch {
      // silent: navigator.share rejects on user cancel or no share target
    }
    // Fallback: відкриваємо файл у новій вкладці (юзер сам save/share)
    window.open(absolute, "_blank", "noopener");
  };

  return (
    <section className="rounded-card bg-gradient-to-br from-amber-500/15 via-rose-500/10 to-cyan-500/10 ring-1 ring-amber-500/30 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Trophy className="w-4 h-4 text-amber-300" />
        <div>
          <div className="text-sm font-semibold">{t("finish_title")}</div>
          <div className="text-[10px] text-fg-muted">
            {t("finish_subtitle")}
          </div>
        </div>
      </div>

      {/* Format toggle */}
      <div className="flex gap-1.5 text-[11px]">
        <button
          type="button"
          onClick={() => setFmt("story")}
          className={`flex-1 px-2 py-1.5 rounded-pill ring-1 transition-colors ${
            fmt === "story"
              ? "bg-accent text-bg-base ring-accent"
              : "bg-bg-raised text-fg-muted ring-bg-divider hover:text-fg-primary"
          }`}
        >
          Story 9:16
        </button>
        <button
          type="button"
          onClick={() => setFmt("square")}
          className={`flex-1 px-2 py-1.5 rounded-pill ring-1 transition-colors ${
            fmt === "square"
              ? "bg-accent text-bg-base ring-accent"
              : "bg-bg-raised text-fg-muted ring-bg-divider hover:text-fg-primary"
          }`}
        >
          Square 1:1
        </button>
      </div>

      {/* Photo picker */}
      <label className="flex items-center gap-2 rounded-pill bg-bg-raised border border-bg-border px-3 py-2 cursor-pointer hover:border-fg-muted transition-colors">
        <Camera className="w-4 h-4 text-fg-muted" />
        <span className="text-[12px] text-fg-secondary flex-1 truncate">
          {photo ? photo.name : t("finish_photo")}
        </span>
        {photo && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setPhoto(null);
              setUrl(null);
            }}
            className="text-[10px] text-fg-muted hover:text-fg-primary"
          >
            {t("photo_remove")}
          </button>
        )}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onPickPhoto}
          className="hidden"
        />
      </label>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onGenerate}
          disabled={gen.isPending}
          className="flex-1 px-3 py-2 rounded-pill bg-accent text-bg-base text-sm font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          {gen.isPending ? (
            <CircleNotch className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <ImageIcon className="w-3.5 h-3.5" />
          )}
          {gen.isPending ? t("generating") : url ? t("regenerate") : t("create")}
        </button>
        {url && (
          <button
            type="button"
            onClick={onNativeShare}
            className="flex-1 px-3 py-2 rounded-pill bg-bg-raised text-fg-primary ring-1 ring-bg-divider text-sm font-medium inline-flex items-center justify-center gap-1.5"
          >
            <ShareNetwork className="w-3.5 h-3.5" />
            {shared ? t("share_done") : t("share")}
          </button>
        )}
      </div>

      {url && (
        <div className="rounded-pill overflow-hidden ring-1 ring-bg-divider bg-bg-base">
          <img
            src={url}
            alt={t("finish_img_alt")}
            className={fmt === "story" ? "w-full aspect-[9/16] object-contain" : "w-full aspect-square object-contain"}
          />
        </div>
      )}
      {gen.isError && (
        <div className="text-[11px] text-rose-300">
          {t("generate_error")}
        </div>
      )}
    </section>
  );
}


/**
 * RaceWeekChecklistSection — the "preparation tool" half of race week.
 *
 * Exported so RaceCenterPage (v2) can embed it inline without
 * duplicating the StageBlock / RaceDayBlock / share-card logic.
 * Self-contained: handles its own loading / error / "no active race"
 * fallbacks based on `useRaceWeek()`. Returns null when there's no
 * coach yet (RaceCenterPage already shows that copy itself).
 */
export function RaceWeekChecklistSection({
  showHero = true,
}: {
  /**
   * Whether to render the gradient race hero (name/date/city). The
   * standalone RaceWeekPage wants it; RaceCenterPage already has its
   * own countdown banner so it passes false.
   */
  showHero?: boolean;
} = {}) {
  const { t, i18n } = useTranslation("race_week");
  const { unit } = useUnitsPreference();
  const { data, isLoading, isError } = useRaceWeek();

  if (isLoading) {
    return <div className="text-fg-muted text-sm">{t("loading")}</div>;
  }
  if (isError) {
    return null;
  }
  if (data && !data.active) {
    return (
      <>
        {data.finisher_goal_id && (
          <FinisherShareCard goalId={data.finisher_goal_id} />
        )}
        <Card variant="indigo" padding={24} className="text-center space-y-3">
          <Flag className="w-10 h-10 mx-auto text-fg-muted" />
          <div className="text-fg-primary font-medium">{t("coming_soon")}</div>
          <div className="text-sm text-fg-muted">{data.note}</div>
        </Card>
      </>
    );
  }
  if (!data?.active) return null;

  return (
    <>
      {data.race_date && (
        <RaceCountdownTimer
          raceDate={data.race_date}
          raceName={data.race_name}
          currentStageKey={data.current_stage_key}
        />
      )}
      {showHero && (
        <div className="rounded-card bg-gradient-to-br from-amber-500/15 via-rose-500/10 to-cyan-500/10 ring-1 ring-amber-500/30 p-4 space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-[10px] uppercase tracking-wider text-fg-muted">
              {data.distance_bucket?.toUpperCase()} · {data.discipline}
            </div>
            {data.days_until_race !== null && data.days_until_race >= 0 && (
              <div className="text-[10px] num-display text-amber-300">
                {data.days_until_race === 0
                  ? t("today")
                  : data.days_until_race === 1
                    ? t("tomorrow")
                    : t("days_until", { count: data.days_until_race })}
              </div>
            )}
          </div>
          <div className="font-semibold text-lg leading-tight">
            {data.race_name || t("title")}
          </div>
          <div className="text-xs text-fg-secondary flex items-center gap-1.5 flex-wrap">
            <Calendar className="w-3 h-3" />
            <span>{fmtRaceDate(data.race_date, i18n.language)}</span>
            {(data.race_city || data.race_country) && (
              <>
                <span>·</span>
                <MapPin className="w-3 h-3" />
                <span>{[data.race_city, data.race_country].filter(Boolean).join(", ")}</span>
              </>
            )}
            {data.distance_km && (
              <>
                <span>·</span>
                <span className="num-display">{formatDistance(data.distance_km, unit, 1)} {unitLabel(unit)}</span>
              </>
            )}
          </div>
          {data.current_stage_key && (
            <div className="text-[11px] text-fg-muted">
              {t("current_phase")}{" "}
              <span className="text-accent">
                {(() => {
                  const s = data.stages.find((x) => x.key === data.current_stage_key);
                  return s ? t(`stage.${s.key}`, { defaultValue: s.title }) : null;
                })()}
              </span>
            </div>
          )}
          <a
            href="/api/athlete/race-week/calendar.ics"
            className="inline-flex items-center gap-1.5 text-[11px] text-cyan-300 hover:text-cyan-200 mt-1"
          >
            <Calendar className="w-3 h-3" />
            <span>{t("download_ics")}</span>
          </a>
        </div>
      )}

      {data.race_day_plan && (
        <RaceDayBlock plan={data.race_day_plan} daysUntil={data.days_until_race ?? 0} />
      )}

      {data.finisher_goal_id && (
        <FinisherShareCard goalId={data.finisher_goal_id} />
      )}

      <div className="space-y-3">
        {data.stages.map((s) => (
          <StageBlock key={s.key} stage={s} />
        ))}
      </div>

      {data.distance_bucket && (
        <RaceWeekGear distanceBucket={data.distance_bucket} />
      )}

      <div className="text-[10px] text-fg-muted text-center pt-2">
        {t("checklist_footer")}
      </div>
    </>
  );
}


export function RaceWeekPage() {
  const { t } = useTranslation("race_week");
  const nav = useNavigate();
  const { isError } = useRaceWeek();

  return (
    <div className="px-4 py-3 space-y-4 pb-12">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => nav(-1)}
          className="p-1 -ml-1 rounded-full hover:bg-bg-raised transition-colors shrink-0 mt-1"
          aria-label={t("back", { ns: "common" })}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <div className="text-[9.5px] uppercase tracking-[0.22em] font-bold" style={{ color: "var(--disc-run)" }}>
            {t("eyebrow", { defaultValue: "RACE WEEK · ПОДГОТОВКА" })}
          </div>
          <h1 className="text-[22px] font-bold text-fg-primary tracking-tight" style={{ letterSpacing: "-0.02em" }}>{t("title")}</h1>
        </div>
      </div>

      {isError ? (
        <Card variant="indigo" padding={24} className="text-center space-y-3">
          <Flag className="w-10 h-10 mx-auto text-fg-muted" />
          <div className="text-fg-primary font-medium">{t("no_coach_title")}</div>
          <div className="text-sm text-fg-muted">{t("no_coach_text")}</div>
          <button
            onClick={() => nav("/marketplace")}
            className="inline-block mt-1 px-4 py-2 rounded-pill bg-accent hover:bg-accent-hover text-accent-on text-sm font-semibold transition-colors"
          >
            {t("find_coach")}
          </button>
        </Card>
      ) : (
        <RaceWeekChecklistSection />
      )}
    </div>
  );
}


function RaceWeekGear({ distanceBucket }: { distanceBucket: string }) {
  const { t } = useTranslation("race_week");
  const { data } = useGear({ recommended_for_distance: distanceBucket });
  const items = (data?.items ?? []).slice(0, 6);
  if (items.length === 0) return null;

  return (
    <div className="rounded-card ring-1 ring-cyan-500/30 bg-cyan-500/5 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Footprints className="w-4 h-4 text-cyan-300" />
        <div className="text-sm font-semibold">{t("gear_title")}</div>
        <Link
          to="/gear"
          className="ml-auto text-[11px] text-cyan-300 hover:text-cyan-200"
        >
          {t("gear_all")}
        </Link>
      </div>
      <div className="text-[11px] text-fg-secondary">
        {t("gear_subtitle")}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {items.map((g) => (
          <Link
            key={g.slug}
            to={`/gear`}
            className="rounded-pill p-2 bg-bg-base ring-1 ring-bg-divider hover:ring-cyan-500/40 transition-colors"
          >
            <div className="text-[10px] uppercase tracking-wider text-fg-muted truncate">
              {g.brand}
            </div>
            <div className="text-[12px] font-medium leading-snug truncate">
              {g.name}
            </div>
            {g.price_eur !== null && (
              <div className="text-[11px] text-accent num-display mt-0.5">
                €{g.price_eur.toFixed(0)}
              </div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
